/**
 * HouseholdOverviewCard — right-column supporting intelligence summary.
 *
 * Concise household-level metrics only — NOT full reports. The detailed
 * findings remain inside individual applicant profiles per the spec.
 *
 * Metrics shown:
 *   • Total Applicants
 *   • Average FICO
 *   • Lowest FICO
 *   • Evictions Found
 *   • Criminal Records Found
 *   • Total Household Income (v1.0.9 — staff-entered, staff-editable)
 */
import { useState } from "react";
import { Users, BarChart3, Pencil } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import HouseholdIncomeEditor from "./HouseholdIncomeEditor";

function MetricRow({ label, value, tone = "neutral", testid, action }) {
  const TONE = {
    good:    "text-emerald-700 bg-emerald-50 ring-emerald-200",
    warn:    "text-amber-700 bg-amber-50 ring-amber-200",
    bad:     "text-rose-700 bg-rose-50 ring-rose-200",
    neutral: "text-slate-700 bg-slate-50 ring-slate-200",
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0" data-testid={testid}>
      <span className="ntn-eyebrow">{label}</span>
      <span className="flex items-center gap-1.5">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ${TONE[tone]}`}>{value}</span>
        {action}
      </span>
    </div>
  );
}

// v1.0.9 — a member FICO is usable for math only when it's a real score:
// not the -9999 insufficient-history sentinel and not status-marked N/A.
function usableMemberFico(m) {
  const d = m.dp || m.decisionpoint || {};
  if (d.fico_status === "insufficient_history" || d.fico_status === "not_available") return null;
  const f = d.fico_score ?? m.fico_score ?? null;
  return typeof f === "number" && f !== -9999 ? f : null;
}

export default function HouseholdOverviewCard({ group, isStaff = false, onUpdated, testid = "household-overview-card" }) {
  const h = group.household || {};
  // v1.0.8 — removed members are history; every fallback aggregate is
  // computed over ACTIVE members only (matches the backend rollup).
  const members = (group.member_screenings || []).filter((m) => !m.removed);
  const totalApplicants = h.active_member_count ?? h.members_count ?? members.length;

  // v1.0.7/v1.0.9 — Average FICO comes from the backend rollup (usable scores
  // only: N/A and -9999 excluded, never imputed as 0). Fallback recomputes
  // from member dp payloads for older cached shapes with the same exclusions.
  const memberFicos = members.map(usableMemberFico).filter((v) => v != null);
  const avgFico = h.fico_avg
    ?? (memberFicos.length
      ? Math.round(memberFicos.reduce((s, v) => s + v, 0) / memberFicos.length)
      : null);
  const lowestFico = h.fico_score ?? (memberFicos.length ? Math.min(...memberFicos) : null);

  // v1.0.6 — null rollup = pending/not-ordered; never render as a clear result.
  const evictionState = h.eviction_records_found;   // true | false | null
  const criminalState = h.criminal_records_found;
  const evictions = evictionState ? (h.eviction_count || 1) : 0;
  const criminal = criminalState ? (h.criminal_count || 1) : 0;

  // v1.0.9 — Total Household Income: staff-entered household-level value,
  // deliberately independent of the sum of individual incomes.
  const thi = group.total_household_income;
  const [incomeOpen, setIncomeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const individualSum = members.reduce((s, m) => s + (Number((m.dp || {}).monthly_income) || 0), 0);

  const openIncome = () => setIncomeOpen(true);
  const saveIncome = async (value) => {
    setBusy(true);
    try {
      await apiClient.patch(`/groups/${group.id}/household-income`, { total_household_income: value });
      toast.success(value == null ? "Total Household Income cleared" : "Total Household Income saved");
      setIncomeOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
          <BarChart3 className="h-4 w-4 text-slate-600" />
        </div>
        <div>
          <div className="ntn-eyebrow">Supporting Intelligence</div>
          <div className="ntn-h3 mt-0.5">Household Overview</div>
        </div>
      </header>

      {/* Top scorecard — total applicants + average FICO */}
      <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100">
        <div className="px-4 py-3 text-center" data-testid={`${testid}-applicants`}>
          <div className="ntn-eyebrow flex items-center justify-center gap-1">
            <Users className="h-3 w-3" /> Total Applicants
          </div>
          <div className="ntn-metric-sm mt-0.5">{totalApplicants}</div>
        </div>
        <div className="px-4 py-3 text-center" data-testid={`${testid}-avgfico`}>
          <div className="ntn-eyebrow">Average FICO</div>
          <div className="ntn-metric-sm mt-0.5">{avgFico ?? "N/A"}</div>
        </div>
      </div>

      {/* Status rows */}
      <div className="px-5 py-1">
        <MetricRow
          label="Lowest FICO"
          value={lowestFico != null ? lowestFico : "N/A"}
          tone={lowestFico == null ? "neutral" : lowestFico >= 670 ? "good" : lowestFico >= 580 ? "warn" : "bad"}
          testid={`${testid}-lowestfico`}
        />
        {evictionState != null && (
          <MetricRow
            label="Evictions Found"
            value={evictions ? `${evictions} record${evictions === 1 ? "" : "s"}` : "None found"}
            tone={evictions ? "bad" : "good"}
            testid={`${testid}-evictions`}
          />
        )}
        {criminalState != null && (
          <MetricRow
            label="Criminal Records"
            value={criminal ? `${criminal} record${criminal === 1 ? "" : "s"}` : "Clear"}
            tone={criminal ? "bad" : "good"}
            testid={`${testid}-criminal`}
          />
        )}
        {group.monthly_rent && (
          <MetricRow
            label="Monthly Rent"
            value={`$${Number(group.monthly_rent).toLocaleString()}`}
            tone="neutral"
            testid={`${testid}-rent`}
          />
        )}
        {(thi != null || isStaff) && (
          <MetricRow
            label="Total Household Income"
            value={thi != null ? `$${Number(thi).toLocaleString()}/mo` : "Not set"}
            tone="neutral"
            testid={`${testid}-household-income`}
            action={isStaff ? (
              <button onClick={openIncome} data-testid="edit-household-income"
                      className="text-slate-400 hover:text-slate-900 transition-colors" title="Edit Total Household Income">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            ) : null}
          />
        )}
      </div>

      <footer className="px-5 py-3 border-t border-slate-100 bg-slate-50/60">
        <p className="ntn-caption leading-snug">
          Household-level summary. Detailed findings live inside individual Applicant Profiles.
        </p>
      </footer>

      {isStaff && (
        <HouseholdIncomeEditor open={incomeOpen} onOpenChange={setIncomeOpen} value={thi}
                               individualSum={individualSum} onSave={saveIncome} busy={busy} />
      )}
    </section>
  );
}
